import React, {useState,useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Button, Popover, Table } from 'antd';
import { alertDeviceService,alertDeviceNotificationService } from '@iso/services';
import AlertDeviceWrapper from './AlertDevice.styles';
import IntlMessages from '@iso/components/utility/intlMessages';
import { SyncOutlined,EnvironmentOutlined } from '@ant-design/icons';
import DeviceStatusLight from './DeviceStatusLight'
import StopDeviceAlarmRequestModal from './StopDeviceAlarmRequestModal';
import { ALERT_DEVICE_TYPE_CHECKIN } from '@iso/constants/alertDeviceTypes';
import styled from "styled-components";
import Box from '@iso/components/utility/box';
import { set } from 'lodash';

// SafeFarm status-light convention:
//   red    = active alarm — nobody is responding and nothing terminated
//   yellow = being attended to OR recently cancelled
//            - any recipient has marked the alert "responding" (Paul Diamond
//              spec: yellow indicates the alert is being attended to), OR
//            - terminate exists and is < 60 min old
//   green  = settled cancellation — terminate > 60 min old
//          OR the alert is a check-in (worker confirmed they're OK)
//
// Check-in handling: a NEYOS device firing `ok`/`user_ok` creates an
// AlertDevice with type='checkin' (see AlertDevice::CHECKIN_TYPE). These
// are not alarms — they're proof-of-life events — so they render green
// immediately regardless of any notification state.
const getDeviceLightColor = (alertDeviceNotifications, alertDeviceType) => {
  if (alertDeviceType === ALERT_DEVICE_TYPE_CHECKIN) {
    return 'green';
  }

  let light = 'red';

  if (alertDeviceNotifications && alertDeviceNotifications.length) {
    const terminated = alertDeviceNotifications.filter((item) => {
      return (
        item?.response_status &&
        item.response_status.toLowerCase() === 'terminate'
      );
    });

    if (terminated.length) {
      const firstTerminated = terminated[0];

      const diffMinutes = Math.floor(
        (new Date() - new Date(firstTerminated.created_at)) / (1000 * 60)
      );

      if (diffMinutes <= 60) {
        light = 'yellow';
      } else {
        light = 'green';
      }
    } else {
      // No termination yet — if anyone is responding, show yellow so
      // the alarm reads as "being attended to" across all views.
      const responding = alertDeviceNotifications.some((item) => {
        return (
          item?.response_status &&
          item.response_status.toLowerCase() === 'responding'
        );
      });
      if (responding) {
        light = 'yellow';
      }
    }
  }

  return light;
};

const AlertDevice = (props) => {
  const {propertyId, setCenterPoint, setPositionDevice, setShowMarkerAlert} = props;
  const currentUser = useSelector((state) => state.user.user);

  const [dataAlert, setDataAlert] = useState([]);
  const [alarmRequests, setAlarmRequests] = useState();
  const [isVisibleAlarmRequestModal, setIsVisibleAlarmRequestModal] = useState(false);
  const [loading, setLoading] = useState(false);

  // Labels match the actual logic in getDeviceLightColor above. The
  // earlier version had red/green swapped — reading the popover
  // suggested red=Cancelled / green=Active, but the code did the
  // opposite. Now corrected: red=active alarm, green=settled or check-in.
  const popoverDeviceStatus =(
    <div>
      <div style={{display:'inline-flex'}}>
        <ColorItemPreview style={{backgroundColor: '#ea5252'}}></ColorItemPreview> &nbsp;&nbsp; Active SOS
      </div> <br/>
      <div style={{display:'inline-flex'}}>
        <ColorItemPreview style={{backgroundColor: '#f6cf67'}}></ColorItemPreview> &nbsp;&nbsp; Responding / Just cancelled (last 60 min)
      </div><br/>
      <div style={{display:'inline-flex'}}>
        <ColorItemPreview style={{backgroundColor: '#00965b'}}></ColorItemPreview> &nbsp;&nbsp; Cancelled / Check-in
      </div>
    </div>
  );

  const handleShowAlarmRequests = (alarmRequests) => {
    alarmRequests = alarmRequests ? alarmRequests : []
    setIsVisibleAlarmRequestModal(true);
    setAlarmRequests(alarmRequests);
  }
   useEffect(() => {
    if(propertyId){
     getListAlert(propertyId);
    }
  },[propertyId]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItem, setTotalItem] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const getListAlert = (propertyId, page = 1, size = 20) => {
    setLoading(true);
    alertDeviceService.get(propertyId, page, size,'alert_device_notifications').then(res => {
      if (res) {
        setDataAlert(res.alert_devices);
        setTotalPages(res.total_page);
        setCurrentPage(res.current_page);
        setTotalItem(res.total_item);
        if(res.page_size){
          setPageSize(res.page_size); 
        }
       
      }
      setLoading(false);
    });
  };

  const columns = [
    {
      title: <IntlMessages id="safeFarmPage.deviceName" />,
      dataIndex: 'device_name',
      key: 'device_name',
    },
    {
      title: <IntlMessages id="uiElements.badge.status" />,
      dataIndex: 'status',
      key: 'status',
      render: (_, record) => {
        const stopRequests = record?.stop_device_alarm_requests;
        const alertDeviceNotifications = record?.alert_device_notifications;
        const light = getDeviceLightColor(alertDeviceNotifications, record?.type);

        return (
          <Popover content={popoverDeviceStatus} title="Status">
            <DeviceStatusLight
              color={light}
              onClick={() => handleShowAlarmRequests((alertDeviceNotifications || []).filter(n => n.is_email_sent || n.is_sms_sent))}
            />
          </Popover>
        );
      },
    },
    {
      title: <IntlMessages id="safeFarmPage.dateTimeAustralia" />,
      dataIndex: 'date_time',
      key: 'date_time',
    },
    {
      title: <IntlMessages id="Location" />,
      dataIndex: 'location',
      key: 'location',
      render: (_, record) => (
        <Button
          className="ant-btn-location"
          onClick={() => getLocation(record)}
          disabled={record.lat == null || record.lng == null}
        >
          <EnvironmentOutlined />
        </Button>
      ),
    },
  ];
  
  const reloadDataAlert = () => {
    setLoading(true);
    if(propertyId){
      getListAlert(propertyId);
    }
  }
  const getLocation = (data) => {
    if (data.lat == null || data.lng == null) return;
    setCenterPoint([data.lat, data.lng]);
    setPositionDevice(data);
    setShowMarkerAlert(true);
  }

  const cancelAlarmModal = () => {
    setIsVisibleAlarmRequestModal(false)
    setAlarmRequests([])
  }

  return (
    <AlertDeviceWrapper>
      <div className='manage-table'>
      <Box id="alert-device-box">
      
      {dataAlert && dataAlert?.length > 0 ?
         <Table
            rowKey="id"
            columns={columns}
            dataSource={dataAlert}
            loading={loading}
            pagination={{
              current: currentPage,
              pageSize: pageSize,
              total: totalItem,
              onChange: (page, size) => {
                getListAlert(propertyId, page, size);
              },
            }}
            size="small"
          />
        :
        
            <h1 style={{ padding: "15px 15px 15px 10px", fontSize: "16px", color: "gray" }}>
              <IntlMessages id="No data found" />
            </h1>
        
        }
      </Box>
      </div> 
      <Button className="reload-button-alert" onClick={reloadDataAlert} >
         { loading ? <SyncOutlined spin /> : <SyncOutlined />}
      </Button>
      <StopDeviceAlarmRequestModal alarmRequests={alarmRequests} visible={isVisibleAlarmRequestModal} cancelModal={cancelAlarmModal} currentUserId={currentUser?.id} onStatusUpdated={() => getListAlert(propertyId)}/>
    </AlertDeviceWrapper>
  );
}

export default AlertDevice;

const ColorItemPreview = styled.div`
  width: 16px;
  height: 16px;
  border-radius: 50%;
`;
